import React, { forwardRef, useEffect, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { Article } from '../../../core/interfaces/article.interface';

type Theme = 'mono' | 'green';

interface ArticleLabelProps {
  article: Article;
  theme?: Theme;
}

// Componente de barcode scannable que se imprime correctamente
function PrintableBarcode({ value }: { value: string }) {
  console.log('PrintableBarcode value:', value);

  // Generar barcode inmediatamente, sin useEffect
  let imageData = '';
  
  try {
    console.log('Generating barcode immediately for:', value);
    
    // Crear canvas temporal
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Generar barcode en el canvas temporal
      JsBarcode(canvas, value, {
        format: "CODE128",
        width: 1.5,
        height: 30,
        displayValue: false,
        background: "#ffffff",
        lineColor: "#000000",
        margin: 0
      });
      
      // Convertir a imagen base64
      imageData = canvas.toDataURL('image/png');
      console.log('Barcode generated successfully as image');
    }
  } catch (error) {
    console.error('Error generating barcode:', error);
  }

  return (
    <div 
      className="barcode-wrap" 
      style={{ height: '11mm', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {imageData ? (
        <img
          src={imageData}
          alt={`Barcode ${value}`}
          style={{
            height: '8mm',
            maxWidth: '100%',
          }}
        />
      ) : (
        <div style={{ 
          height: '8mm', 
          width: '100%', 
          background: '#f0f0f0', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          fontSize: '2mm',
          color: '#666'
        }}>
          Error generando barcode
        </div>
      )}
    </div>
  );
}

const themes: Record<Theme, {
  bg: string;           // fondo etiqueta
  frameBorder: string;  // borde exterior
  bandBg: string;       // franja superior (fondo)
  bandColor: string;    // franja superior (texto)
  textColor: string;    // textos
  accent?: string;      // solo para green
}> = {
  mono: {
    bg: '#FFFFFF',
    frameBorder: '0.35mm solid #BDBDBD',
    bandBg: '#FFFFFF',
    bandColor: '#000000',
    textColor: '#111111',
  },
  green: {
    bg: '#FFFFFF',
    frameBorder: '0.35mm solid #7CB342',
    bandBg: '#2E7D32',
    bandColor: '#E8F5E9',
    textColor: '#1B5E20',
    accent: '#8BC34A',
  },
};

export const ArticleLabel = forwardRef<HTMLDivElement, ArticleLabelProps>(({ article, theme = 'mono' }, ref) => {
  const t = themes[theme];

  const code = (article.code || '123456789012345678901234').toUpperCase();
  const rawDesc = (article.description || '').toUpperCase().replace(/\s{2,}/g, ' ').trim();
  const fallbackDesc =
    'SIN DESCRIPCIÓN ASIGNADA';
  const normalizedDesc = (rawDesc.length > 0 ? rawDesc : fallbackDesc).replace(/[^A-Z0-9\s.,/-]/g, '');

  const MAX_CHARACTERS = 120;
  const lineWidth = 40; // caracteres aproximados por línea en 60mm
  const truncatedDesc = normalizedDesc.slice(0, MAX_CHARACTERS);
  const displayDesc = normalizedDesc.length > MAX_CHARACTERS ? `${truncatedDesc}…` : truncatedDesc;

  const descriptionLines: string[] = [];
  for (let i = 0; i < displayDesc.length; i += lineWidth) {
    descriptionLines.push(displayDesc.slice(i, i + lineWidth).trim());
  }
  if (descriptionLines.length === 0) {
    descriptionLines.push(displayDesc);
  }

  return (
    <div
      ref={ref}
      style={{
        width: '60mm',
        height: '30mm',
        background: t.bg,
        boxSizing: 'border-box',
        padding: '1.2mm',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '1.2mm',
        border: t.frameBorder,
      }}
      className="print-label print-label-60x30"
    >
      {/* (Opcional) muesca/ornamento solo para tema green */}
      {theme === 'green' && (
        <div
          style={{
            position: 'absolute',
            top: '1.2mm',
            left: '1.2mm',
            width: '9mm',
            height: '9mm',
            background: `linear-gradient(135deg, transparent 0 49%, ${t.accent} 49% 100%)`,
            borderTopLeftRadius: '1mm',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Franja superior con el código en grande (en mono es blanca) */}
      <div
        style={{
          height: '6mm',
          background: t.bandBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 2mm',
          textAlign: 'center',
          borderBottom: theme === 'mono' ? '0.3mm solid #BDBDBD' : 'none',
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: '3mm',
            letterSpacing: '0.2mm',
            color: t.bandColor,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
          }}
        >
          {code}
        </span>
      </div>

      {/* Barcode real CODE128 usando bwip-js.toSVG() */}
      <div
        style={{
          height: '11mm',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1mm 2mm 0',
        }}
      >
        <PrintableBarcode value={code} />
      </div>

      {/* Descripción (hasta 120 caracteres) */}
      <div
        style={{
          position: 'absolute',
          left: '2mm',
          right: '2mm',
          bottom: '2mm',
          textAlign: 'center',
          color: t.textColor,
          fontWeight: 600,
          lineHeight: 1.1,
          fontSize: '2.2mm',
          letterSpacing: 0,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          textTransform: 'uppercase',
        }}
      >
        {descriptionLines.map((line, index) => (
          <div
            key={`${line}-${index}`}
            style={{
              whiteSpace: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {line}
          </div>
        ))}
      </div>
    </div>
  );
});

ArticleLabel.displayName = 'ArticleLabel';

export default ArticleLabel;
